
import React, { useState } from "react";
import MediaImage from "./MediaImage";

/**
 * Portrait image carousel (Resources grounding techniques).
 *
 * @param {object} props
 * @param {Array<{media?: object, src?: string, alt?: string, caption?: string}>} props.images -
 *   Each slide is a §3.13.2 image media object (`media`, rendered by
 *   MediaImage) or a plain `src`/`alt` pair, with an optional caption.
 */
export default function GroundingCarousel({ images = [] }) {
  const [idx, setIdx] = useState(0);
  const total = images.length;
  // The list can shrink when the API copy replaces the bundled one.
  if (total && idx >= total) setIdx(0);

  const go = (n) => setIdx((prev) => (prev + n + total) % total);
  const goTo = (i) => setIdx(i);

  return (
    <div
      className="carousel"
      role="region"
      aria-roledescription="carousel"
      aria-label="Grounding images carousel"
    >
      <div className="car-viewport">
        <div
          className="car-track"
          style={{ transform: `translateX(-${idx * 100}%)` }}
        >
          {images.map((img, i) => (
            <div
              className="car-slide portrait"
              key={i}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${total}`}
              aria-hidden={i !== idx}
            >
              {img.media ? (
                <MediaImage media={img.media} loading="eager" />
              ) : (
                <img src={img.src} alt={img.alt || `Grounding image ${i + 1}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Optional visible text summary of the current image */}
      {images[idx] && images[idx].caption && (
        <p className="car-caption" aria-live="polite">{images[idx].caption}</p>
      )}

      <button type="button" className="car-btn left" onClick={() => go(-1)} aria-label="Previous slide">‹</button>
      <button type="button" className="car-btn right" onClick={() => go(1)} aria-label="Next slide">›</button>

      <div className="car-dots">
        {images.map((_, i) => (
          <button
            type="button"
            key={i}
            className={`dot ${i === idx ? "active" : ""}`}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            aria-current={i === idx ? "true" : undefined}
          />
        ))}
      </div>
    </div>
  );
}
